import { useEffect, useState } from 'react'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import { Busy, ErrorBanner, Field, Notice, TextInput } from '../ui/controls'
import type { MessageKey } from '../i18n/messages'
import type { ConnectedContacts, ConnectionState } from '../api/types'

/**
 * L3 — the only place a name and a way to reach someone ever arrive.
 *
 * 决议 §2.1's red line: 任何付费都买不到. Not the $20 report, not the $10 early unlock — only a
 * live connection, and **both sides at the same moment**. The page says that out loud, because
 * the other person is reading the same thing about you right now and that is the point.
 */
export function ConnectionPage() {
  const { t } = useI18n()

  const [state, setState] = useState<ConnectionState | null | undefined>(undefined)
  const [contacts, setContacts] = useState<ConnectedContacts | null>(null)
  const [reason, setReason] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const load = () => {
    api.connection
      .current()
      .then((current) => {
        setState(current)

        if (current) {
          return api.connection.contacts().then(setContacts)
        }

        setContacts(null)
        return undefined
      })
      .catch(setError)
  }

  useEffect(load, [])

  if (state === undefined) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  if (state === null) {
    return (
      <div className="page stack">
        <h1>{t('connection.title')}</h1>
        <Notice>{t('connection.none')}</Notice>
      </div>
    )
  }

  const name = contacts
    ? [contacts.givenName, contacts.familyName].filter(Boolean).join(' ') ||
      contacts.preferredName ||
      ''
    : ''

  return (
    <div className="page stack">
      <h1>{t('connection.title')}</h1>

      <ErrorBanner error={error} />

      <section className="card stack">
        <div className="kv">
          <span>{t('connection.since')}</span>
          <strong>{new Date(state.connectedAt).toLocaleDateString()}</strong>
        </div>

        {contacts ? (
          <>
            <div className="kv">
              <span>{t('connection.name')}</span>
              <strong>{name}</strong>
            </div>

            <h2>{t('connection.contacts')}</h2>

            <ul className="contact-list">
              {contacts.contactMethods.map((method) => (
                <li key={method.id}>
                  <span className="field-hint">
                    {t(`contact.type.${method.methodType}` as MessageKey)}
                  </span>
                  <strong>{method.value}</strong>
                </li>
              ))}
            </ul>

            <Notice>{t('connection.bothSides')}</Notice>
          </>
        ) : (
          <Busy />
        )}
      </section>

      <section className="card stack">
        <p className="field-hint">{t('connection.closeWarning')}</p>

        {confirming ? (
          <>
            <Field label={t('connection.closeReason')}>
              <TextInput value={reason} onChange={setReason} maxLength={100} />
            </Field>

            <div className="row-links">
              <button
                type="button"
                className="danger"
                disabled={busy}
                onClick={() => {
                  setError(null)
                  setBusy(true)

                  api.connection
                    .close(state.connectionId, reason.trim() === '' ? null : reason.trim())
                    .then(() => {
                      setConfirming(false)
                      load()
                    })
                    .catch(setError)
                    .finally(() => setBusy(false))
                }}
              >
                {t('connection.confirm')}
              </button>

              <button type="button" className="link-button" onClick={() => setConfirming(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </>
        ) : (
          <button type="button" className="danger" onClick={() => setConfirming(true)}>
            {t('connection.close')}
          </button>
        )}
      </section>
    </div>
  )
}
